import {
  type Action,
  type CryptoCurrencyDataSource,
  type FiatCurrency,
  setActionPrices,
} from '@0xtorch/core'
import { saveApiData } from '@pkg/database-portfolio'
import type {
  ApiConfig,
  AssetRecord,
  FetchApiData,
  SignAsterV3Payload,
} from '@pkg/exchange-api'
import type { OnUpdateApiProgressCallback, RunToDatabase } from '../types'

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

type ApiPending = {
  id: number
  apiId: number
  from: number
  to: number
}

type SyncPendings = (params: {
  accountId: number
  apiId: number
  apiKey: string
  apiPassphrase?: string
  apiSecret?: string
  signAsterV3?: SignAsterV3Payload
  assets: AssetRecord
  config: ApiConfig
  cryptoCurrencyDataSource: CryptoCurrencyDataSource
  fiat: FiatCurrency
  pendings: ApiPending[]
  proxy?: string
  /**
   * 同期開始時刻 (account.apiStartTime ?? portfolio.startTime)。
   * DB の pending は古い設定で記録されることがあるため、ここで指定した時刻より
   * 前の期間は API call せずに pending を進める。
   */
  startTime: number
  subType?: string
  symbolAssetMap: Record<string, string>
  fetchApiData: FetchApiData
  getToken?: () => Promise<string | null>
  onUpdateProgress?: OnUpdateApiProgressCallback
  writeToDatabase: RunToDatabase
}) => Promise<void>

export const syncPendings: SyncPendings = async ({
  accountId,
  apiId,
  apiKey,
  apiPassphrase,
  apiSecret,
  signAsterV3,
  assets,
  config,
  cryptoCurrencyDataSource,
  fiat,
  pendings,
  proxy,
  startTime,
  subType,
  symbolAssetMap,
  fetchApiData,
  getToken,
  onUpdateProgress,
  writeToDatabase,
}) => {
  // 同期済期間 (ms)
  let synced = 0

  // API Pending 古い順に API データ同期＆保存
  const remainPendings = pendings.map((p) => ({
    ...p,
  }))
  remainPendings.sort((a, b) => a.from - b.from)
  while (remainPendings.length > 0) {
    const pending = remainPendings[0]

    // ユーザーが設定した同期開始日 (account.apiStartTime / portfolio.startTime) より前は
    // API call せずに pending を進める。 DB の pending は古い設定で記録されることがあるため、
    // ここで現在の startTime に合わせてクランプする。
    const skipBefore = startTime
    if (pending.from < skipBefore) {
      if (pending.to < skipBefore) {
        // pending 期間全体が同期対象外 — pending を削除して次へ
        await writeToDatabase((database) =>
          saveApiData({
            accountId,
            actions: [],
            apiId,
            database,
            pendingChange: {
              id: pending.id,
              action: 'delete',
              from: pending.from,
              to: pending.to,
            },
            sources: [],
          }),
        )
        synced += pending.to - pending.from
        remainPendings.shift()
        if (onUpdateProgress) {
          onUpdateProgress({ synced })
        }
        continue
      }
      // pending の from を skipBefore までクランプして DB に反映
      await writeToDatabase((database) =>
        saveApiData({
          accountId,
          actions: [],
          apiId,
          database,
          pendingChange: {
            id: pending.id,
            action: 'update',
            from: skipBefore,
            to: pending.to,
          },
          sources: [],
        }),
      )
      synced += skipBefore - pending.from
      pending.from = skipBefore
      if (onUpdateProgress) {
        onUpdateProgress({ synced })
      }
    }

    // config に合わせて from/to を決定
    let from: number | undefined
    let to: number | undefined
    switch (config.order) {
      case 'asc': {
        from = pending.from
        if (config.range) {
          to = Math.min(pending.to, pending.from + config.range - 1)
        } else if (config.requiresRange) {
          to = pending.to
        } else {
          to = undefined
        }
        break
      }
      case 'desc': {
        to = pending.to
        if (config.range) {
          from = Math.max(pending.from, pending.to - config.range + 1)
        } else if (config.requiresRange) {
          from = pending.from
        } else {
          from = undefined
        }
        break
      }
      default: {
        throw new Error(`Unsupported order: ${config.order satisfies never}`)
      }
    }

    // API データ取得
    const {
      actions,
      count,
      from: fetchedFrom,
      to: fetchedTo,
      sources,
      stopped,
    } = await fetchApiData({
      apiKey,
      apiPassphrase,
      apiSecret,
      signAsterV3,
      assets,
      config,
      from,
      createHeaders: getToken
        ? async (): Promise<Record<string, string>> => {
            const token = await getToken()
            if (!token) {
              return {}
            }
            return {
              Authorization: `Bearer ${token}`,
            }
          }
        : undefined,
      proxy,
      symbolAssetMap,
      subType,
      to,
    })

    // pending.from/to 範囲外のデータ除外
    const filteredActions = actions.filter(
      (action) =>
        action.timestamp >= pending.from && action.timestamp <= pending.to,
    )

    // actions の reorder & price 更新
    const reorderedActions = reorderActions(filteredActions)
    const pricedActions = await setActionPrices({
      actions: reorderedActions,
      fiat,
      dataSource: cryptoCurrencyDataSource,
    })

    // pending ステータス更新
    // - stopped = false で count が config.limit 未満なら pending 期間全処理完了
    // [order = 'asc' の場合]
    // - from は min(fetchedTo, to)
    // - to は pending.to
    // [order = 'desc' の場合]
    // - from は pending.from
    // - to は max(fetchedFrom, from)
    const fullyFetched =
      (fetchedFrom && fetchedFrom < pending.from) ||
      (fetchedTo && fetchedTo > pending.to) ||
      (!stopped &&
        count < config.limit &&
        (from === undefined || from === pending.from) &&
        (to === undefined || to === pending.to))
    const newFrom =
      config.order === 'asc'
        ? count < config.limit
          ? (to ?? pending.to) + 1
          : // 同一 timestamp の取引で limit 分以上ある場合に 1ms ズラす
            fetchedTo && fetchedTo === fetchedFrom && from === fetchedFrom
            ? fetchedTo + 1
            : Math.min(fetchedTo ?? Number.POSITIVE_INFINITY, to ?? pending.to)
        : pending.from
    const newTo =
      config.order === 'desc'
        ? count < config.limit
          ? (from ?? pending.from) - 1
          : // 同一 timestamp の取引で limit 分以上ある場合に 1ms ズラす
            fetchedFrom && fetchedFrom === fetchedTo && to === fetchedTo
            ? fetchedFrom - 1
            : Math.max(fetchedFrom ?? 0, from ?? pending.from)
        : pending.to

    // DB に保存・更新
    await writeToDatabase((database) =>
      saveApiData({
        accountId,
        actions: [...pricedActions],
        apiId,
        database,
        pendingChange: {
          id: pending.id,
          action: fullyFetched ? 'delete' : 'update',
          // stopped = true & order = 'desc' の場合、 from/to は pending の from/to 使用（更新しない）
          from: stopped && config.order === 'desc' ? pending.from : newFrom,
          to: stopped && config.order === 'desc' ? pending.to : newTo,
        },
        sources,
      }),
    )

    // stopped = true の場合、処理終了
    if (stopped) {
      // TODO 処理停止用のエラーを投げる
      break
    }

    // pending 更新
    if (fullyFetched) {
      remainPendings.shift() // 完了した pending を削除
      synced += (to ?? pending.to) - (from ?? pending.from)
    } else {
      synced += newFrom - pending.from + (pending.to - newTo)
      pending.from = newFrom
      pending.to = newTo
    }

    // 進捗更新
    if (onUpdateProgress) {
      onUpdateProgress({
        synced,
      })
    }

    // レートリミット対策: 次の request まで待機
    if (remainPendings.length > 0 && config.requestDelay) {
      await sleep(config.requestDelay)
    }
  }
}

const reorderActions = (actions: Action[]): Action[] => {
  const actionCounts = new Map<string, number>()
  for (const action of actions) {
    const count = actionCounts.get(action.source) || 0
    action.order = count
    actionCounts.set(action.source, count + 1)
  }
  return actions
}
